import React from 'react';
import { ArrowLeftIcon, ClockIcon } from '@heroicons/react/24/outline';
import { BlogPost } from './types';

interface BlogPostViewProps {
  post: BlogPost;
  onBack: () => void;
}

const BlogPostView: React.FC<BlogPostViewProps> = ({ post, onBack }) => {
  const ContentComponent = post.content;

  return (
    <main className="min-h-screen text-text-main">
      <article className="mx-auto max-w-4xl px-4 md:px-6 py-20 md:py-24">
        
        {/* Back Navigation */}
        <button
          type="button"
          onClick={onBack}
          className="group mb-12 inline-flex items-center gap-2 text-sm font-medium text-text-muted transition-colors hover:text-primary"
        >
          <ArrowLeftIcon className="h-4 w-4 transition-transform group-hover:-translate-x-1" />
          Back to all articles
        </button>

        {/* Article Header */}
        <header className="mb-16 space-y-6">
          <div className="flex flex-wrap items-center gap-3 text-sm">
            <span className="inline-flex items-center gap-2 rounded-full bg-primary/10 px-3 py-1.5 text-xs font-semibold text-primary">
              <post.icon className="h-3.5 w-3.5" />
              {post.category}
            </span>
            <span className="text-text-muted/80">{post.date}</span>
            <span className="text-text-muted/40">•</span>
            <span className="inline-flex items-center gap-1.5 text-text-muted/80">
              <ClockIcon className="h-3.5 w-3.5" />
              {post.readTime}
            </span>
          </div>
          
          <h1 className="text-4xl font-bold leading-tight text-text-main md:text-5xl lg:text-6xl">
            {post.title}
          </h1>
          
          <p className="text-xl leading-relaxed text-text-muted">
            {post.excerpt}
          </p>
        </header>

        {/* Article Content */}
        <div className="prose-custom mb-16">
          <ContentComponent />
        </div>

        {/* Article Footer */}
        <footer className="border-t border-border-glow/30 pt-10">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <button
              type="button"
              onClick={onBack}
              className="group inline-flex items-center gap-2 text-sm font-medium text-text-muted transition-colors hover:text-primary"
            >
              <ArrowLeftIcon className="h-4 w-4 transition-transform group-hover:-translate-x-1" />
              More articles
            </button>
            <button
              type="button"
              onClick={() => {
                const ev = new CustomEvent('navigateToPage', { detail: { page: 'home', startProcessing: true } });
                window.dispatchEvent(ev);
              }}
              className="inline-flex items-center gap-2 rounded-full bg-primary/20 px-6 py-3 text-sm font-semibold text-primary transition-all hover:bg-primary/30 hover:shadow-lg hover:shadow-primary/20"
            >
              Try NotesCrafter Free
              <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
              </svg>
            </button>
          </div>
        </footer>
      </article>
    </main>
  );
};

export default BlogPostView;
